// Talking Head Overlay's form (live's `talking-head-overlay-form` library view, spec G14-templates
// PR 13): the base video, the AI Actor and its voice, where the speech comes from (a script read
// by the voice, its voiceover made by Generate Audio, or the base video's own audio), and the
// place, one of live's 9 cells. One tile: the actor speaking, cut out, over the base.
import { Group, Radio, Stack, Text, Textarea, Tooltip } from "@mantine/core";
import { AudioLines, Film, Info, Locate } from "lucide-react";
import { useState } from "react";

import { useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import type { Catalog, OverlayPosition, Voice } from "../../../api/generated/model";
import { VideoTemplateQuote, VideoTemplateRequest } from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { AddActorsDialog } from "../../../platform/actors/AddActorsDialog";
import { ActorsComposer } from "../../../platform/actors/composer/ActorsComposer";
import { pickedOfActor, type PickedActor } from "../../../platform/actors/picked";
import { useMakings, type Making } from "../../../platform/actors/use-makings";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import type { PickedImage } from "../../../platform/media/ImagePicker";
import { ScriptActions, ScriptBusy } from "../../../platform/scripts/ScriptActions";
import { useScriptJobs } from "../../../platform/scripts/use-script-jobs";
import { useVoicePages } from "../../../platform/voices/VoiceList";
import { VoicePanel } from "../../../platform/voices/VoicePanel";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import { AUDIO_NOTE, CostChip } from "../TemplateCost";
import { ActorTile } from "./ActorTile";
import classes from "./forms.module.css";
import { ReuseSwitch, StartArrow, VideoSlot, type TemplateFormProps } from "./kit";
import { ScriptOverlayWithProduct } from "./ScriptOverlayWithProduct";
import { useVoiceover, VOICEOVER_MODEL, VoiceAudioTile, VoiceoverButton, VoiceWaits } from "./voiceover";

/** Live's words (`library-view/constants.ts`, `overlay-positions.ts`), verbatim. */
const BASE_TIP =
  "The main video your Talking Head will appear on top of. Select one from your library or upload a new file.";
const ACTOR_TIP = "Choose an existing AI Actor or generate a brand-new one to be your on-screen speaker.";
const SOURCE_TIP =
  "Decide where the audio comes from: write a fresh script, or reuse the audio already in your base video.";
const POSITION_TIP = "Choose where the Talking Head overlay sits on screen.";
const SCRIPT_PLACEHOLDER =
  "Write your script... Use [emotion] to add emotions like [laughs], [sights], [excited]";
const BRIEF_PLACEHOLDER =
  "Describe what your presenter should talk about — the product, the audience, the tone…";
const SCRIPT_MAX = 10000;
/** Live's chain's models (`useOverlayModels`): the head and the cut-out (ours by id). */
const HEAD_MODEL = "veed-fabric-1-0";
const CUTOUT_MODEL = "veed-video-background-removal";
const POSITIONS: { value: OverlayPosition; label: string }[] = [
  { value: "top-left", label: "Top left" },
  { value: "top-center", label: "Top center" },
  { value: "top-right", label: "Top right" },
  { value: "middle-left", label: "Middle left" },
  { value: "center", label: "Center" },
  { value: "middle-right", label: "Middle right" },
  { value: "bottom-left", label: "Bottom left" },
  { value: "bottom-center", label: "Bottom center" },
  { value: "bottom-right", label: "Bottom right" },
];

type Source = "script" | "base_video_audio";

/** The voice the head speaks with: one picked in Voice Settings, or the actor's own. */
type VoiceChoice = Pick<Voice, "id" | "name" | "kind">;

/** Live's words for the chain's models a team is not offered, in one line (`useOverlayModels`). */
function missingModels(catalog: Catalog | undefined): string | null {
  if (catalog === undefined) return null;
  const missing = [
    ...(catalog.video_models.some((each) => each.id === HEAD_MODEL) ? [] : ["talking-head"]),
    ...(catalog.video_models.some((each) => each.id === CUTOUT_MODEL) ? [] : ["background-removal"]),
    ...(catalog.audio_models.some((each) => each.id === VOICEOVER_MODEL) ? [] : ["text-to-speech"]),
  ];
  return missing.length === 0 ? null : `No ${missing.join(", ")} model is available for this team`;
}

/** Live's 3x3 icon of a cell (`overlay-position-select`): the picked one marked. */
function CellGlyph({ at }: { at: number }) {
  return (
    <span className={classes.cells} aria-hidden>
      {Array.from({ length: 9 }, (_, cell) => (
        <span key={cell} className={classes.cell} data-on={cell === at || undefined} />
      ))}
    </span>
  );
}

/**
 * Live's form: "Reuse example" (off and disabled: the example follows), the Base video slot
 * (From library, Upload), the AI Actor tile (From Library, Add AI Actor) and the voice tile
 * (Voice Settings, greyed with the base video's audio; once made, the voiceover's player), Voice
 * source (Script, the default, or Base video audio, which closes Voice Settings), the script
 * with its tools and Generate Audio (the voiceover, made before the run), or live's card with
 * the base video's audio, and in the footer the place (Bottom right by default) and the round
 * start, with no tooltip, as live's. While the voiceover is made or the run starts, every field
 * is greyed, as live's (`isBusy`).
 */
export function TalkingHeadOverlayForm(props: TemplateFormProps) {
  const { teamId, brandId, above, onStarted } = props;
  const [collapsed, setCollapsed] = useState(false);
  const [base, setBase] = useState<PickedImage | null>(null);
  const [actor, setActor] = useState<PickedActor | null>(null);
  const [voice, setVoice] = useState<VoiceChoice | null>(null);
  const [source, setSource] = useState<Source>("script");
  const [script, setScript] = useState("");
  const [position, setPosition] = useState<OverlayPosition>("bottom-right");
  const [library, setLibrary] = useState(false);
  const [makingActor, setMakingActor] = useState(false);
  const [makings, setMakings] = useState<Making[]>([]);
  const [voicePanel, setVoicePanel] = useState(false);
  const [scriptOpen, setScriptOpen] = useState(false);
  const catalog = useGetCatalog().data;
  // Only for Voice Settings' "Recommended Voice" row, which plays the actor's own voice.
  const globals = useVoicePages(teamId, brandId, { kind: "global" });
  const voices = globals.data?.pages.flatMap((page) => page.items) ?? [];
  const recommended = actor?.voice == null ? null : (voices.find((each) => each.id === actor.voice?.id) ?? null);
  const voiceover = useVoiceover(teamId, brandId);
  const write = (text: string) => {
    setScript(text);
    voiceover.clear();
  };
  // Another voice is not what the voiceover says: it goes; the same one keeps it, as live's.
  const chooseVoice = (next: VoiceChoice | null) => {
    if (next?.id !== voice?.id) voiceover.clear();
    setVoice(next);
  };
  // Live's actor pick (`actor-tile-field`): an actor with its own voice (its
  // `recommendedVoice`, a global actor's) brings it, whatever page it is on; one without keeps
  // the voice chosen.
  const chooseActor = (next: PickedActor | null) => {
    setActor(next);
    if (next?.voice != null) chooseVoice({ id: next.voice.id, name: next.voice.name, kind: "global" });
  };
  const chooseSource = (next: Source) => {
    setSource(next);
    // Voice Settings belongs to the script: the base video's own audio closes it, as live's.
    if (next === "base_video_audio") setVoicePanel(false);
  };
  useMakings({
    teamId,
    brandId,
    makings,
    onMade: (jobId, made) => {
      setMakings((current) => current.filter((each) => each.jobId !== jobId));
      chooseActor(pickedOfActor(made));
    },
    onFailed: (jobId) =>
      setMakings((current) => current.map((each) => (each.jobId === jobId ? { ...each, failed: true } : each))),
  });
  const scripts = useScriptJobs({ teamId, brandId, tool: "templates", onScript: write });
  const busyScript = scripts.processing || scripts.enhancing;
  const fromBase = source === "base_video_audio";
  const made = voiceover.made;
  const missing = missingModels(catalog);
  const inputs =
    base === null || actor === null || (!fromBase && made === null) || missing !== null
      ? null
      : {
          template: "talking-head-overlay" as const,
          base_video: { kind: "asset" as const, asset_id: base.assetId },
          actor: actor.source,
          voice_source: source,
          ...(fromBase || made === null ? {} : { audio: made.ref }),
          position,
        };
  const body = inputs === null ? null : { type: "video.template" as const, inputs };
  const request = VideoTemplateRequest.safeParse(body);
  const quote = VideoTemplateQuote.safeParse(body);
  const job = usePricedStart({
    teamId,
    brandId,
    quote: quote.success ? quote.data : undefined,
    request: request.success ? request.data : undefined,
    onStarted,
  });
  const busy = voiceover.running || job.start.isPending;
  if (makingActor && catalog !== undefined) {
    // Live's "Add AI Actor" (`actor-generation-shell`): the actor composer in the form's place.
    const close = () => setMakingActor(false);
    return (
      <ActorsComposer
        teamId={teamId}
        brandId={brandId}
        catalog={catalog}
        onStarted={(started) => {
          setMakings((current) => [...current, { jobId: started.id, failed: false }]);
          close();
        }}
        place={{
          outputs: 1,
          uploadMost: 1,
          onCancel: close,
          onCreated: (actors) => {
            const first = actors[0];
            if (first !== undefined) chooseActor(pickedOfActor(first));
            close();
          },
        }}
      />
    );
  }
  return (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      // No Reuse example yet: live's carries a template voice (G14's gap), so the switch stays off.
      header={<ReuseSwitch example={null} checked={false} onChange={() => undefined} />}
      summary={<Text size="sm">Talking Head Overlay</Text>}
      overlay={
        scriptOpen ? (
          <ScriptOverlayWithProduct
            teamId={teamId}
            brandId={brandId}
            placeholder={BRIEF_PLACEHOLDER}
            pending={scripts.generating}
            error={scripts.generateError}
            onGenerate={(brief, productId) => scripts.generate(brief, productId, () => setScriptOpen(false))}
            onCancel={() => setScriptOpen(false)}
          />
        ) : undefined
      }
      aside={
        voicePanel && !fromBase ? (
          <VoicePanel
            teamId={teamId}
            brandId={brandId}
            chosen={voice}
            recommended={recommended}
            // Locked while a voiceover is made: another voice would forget a speech being paid for.
            onChoose={(next) => (voiceover.running ? undefined : chooseVoice(next))}
            head={<VoiceWaits running={voiceover.running} />}
            onClose={() => setVoicePanel(false)}
            designModel={catalog?.audio_models.find((each) => each.job_types.includes("voice.design"))}
          />
        ) : undefined
      }
      footer={
        <Group justify="space-between" wrap="nowrap" gap={12}>
          <Choice
            setting="Position"
            icon={<Locate size={16} strokeWidth={1.5} />}
            tip={POSITION_TIP}
            width={150}
            variant="footer"
            value={position}
            options={POSITIONS.map((each, cell) => ({ ...each, icon: <CellGlyph at={cell} /> }))}
            disabled={busy}
            onChange={(next) => setPosition(next as OverlayPosition)}
          />
          <Group gap={8} wrap="nowrap">
            {/* Live's: one output; the note only while a script makes the voice (Generate Audio). */}
            <CostChip
              estimate={props.template.estimate}
              outputs={1}
              quote={job.lastPriced}
              {...(fromBase ? {} : { note: AUDIO_NOTE })}
            />
            <StartArrow canStart={job.canStart && !busy} starting={job.start.isPending} onStart={job.startJob} />
          </Group>
        </Group>
      }
    >
      <Stack gap={12}>
        <Group gap="xs" wrap="nowrap" align="flex-start">
          <VideoSlot
            teamId={teamId}
            brandId={brandId}
            label="Base video"
            icon={<Film size={20} strokeWidth={1.5} aria-hidden />}
            subtitle="Pick the video your Talking Head is placed on"
            tall
            tooltip={BASE_TIP}
            value={base}
            disabled={busy}
            // No example clip here (the Overlay has no example): a library video alone.
            onChange={(value) => setBase(value === null || "exampleKey" in value ? null : value)}
          />
          <ActorTile
            actor={actor}
            making={makings[0] ?? null}
            tip={ACTOR_TIP}
            onFromLibrary={() => setLibrary(true)}
            onMakeActor={catalog === undefined ? null : () => setMakingActor(true)}
            onRemove={() => chooseActor(null)}
            onDropMaking={() => setMakings([])}
            disabled={busy}
          />
          <VoiceAudioTile
            voiceName={voice?.name ?? null}
            voiceover={voiceover}
            tip={null}
            disabled={fromBase || job.start.isPending}
            onOpen={() => setVoicePanel(true)}
          />
        </Group>
        <Group gap={6} wrap="nowrap">
          <Text size="sm" fw={500}>
            Voice source
          </Text>
          <Tooltip label={SOURCE_TIP} multiline maw={280} withArrow>
            <Info size={14} aria-label="About the voice source" />
          </Tooltip>
        </Group>
        {/* Live's `RadioCard`: outlined pills, the picked one primary, no check. */}
        <Radio.Group value={source} onChange={(next) => chooseSource(next as Source)}>
          <Group gap="xs">
            <Radio.Card value="script" className={classes.pill} disabled={busy}>
              Script
            </Radio.Card>
            <Radio.Card value="base_video_audio" className={classes.pill} disabled={busy}>
              Base video audio
            </Radio.Card>
          </Group>
        </Radio.Group>
        {fromBase ? (
          <div className={classes.baseAudio}>
            <span className={classes.baseAudioIcon}>
              <AudioLines size={20} strokeWidth={1.5} aria-hidden />
            </span>
            <Stack gap={4}>
              <Text className={classes.baseAudioTitle}>Using audio from your base video</Text>
              <Text className={classes.baseAudioLine}>Script and voice settings aren’t needed.</Text>
            </Stack>
          </div>
        ) : (
          <>
            <Group justify="space-between" wrap="nowrap">
              <Text size="sm" fw={500}>
                Script
              </Text>
              <ScriptActions
                canGenerate={!busyScript && !busy}
                canEnhance={!busyScript && !busy && script.trim() !== ""}
                enhancing={scripts.enhancing}
                failed={scripts.scriptFailed}
                onGenerate={() => setScriptOpen(true)}
                onEnhance={() => scripts.enhance(script)}
              />
            </Group>
            <ScriptBusy busy={scripts.processing}>
              <Textarea
                variant="unstyled"
                aria-label="Script"
                placeholder={SCRIPT_PLACEHOLDER}
                autosize
                minRows={2}
                maxRows={8}
                maxLength={SCRIPT_MAX}
                disabled={scripts.processing || busy}
                value={script}
                onChange={(event) => write(event.currentTarget.value)}
              />
            </ScriptBusy>
            <VoiceoverButton
              voiceover={voiceover}
              script={script}
              voiceId={voice?.id ?? null}
              disabled={busyScript || job.start.isPending}
            />
          </>
        )}
        {missing === null ? null : (
          <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
            {missing}
          </Text>
        )}
        {voiceover.error === null ? null : <ErrorAlert error={voiceover.error} />}
        {job.quoteError === null ? null : <ErrorAlert error={job.quoteError} />}
        {job.start.isError ? <ErrorAlert error={job.start.error} /> : null}
        {/* Live's toast, said in the panel (web/AGENTS.md: no toasts). */}
        {job.start.isSuccess ? (
          <Text component="output" size="sm">
            Generation started
          </Text>
        ) : null}
      </Stack>
      {library ? (
        <AddActorsDialog
          teamId={teamId}
          brandId={brandId}
          chosen={actor === null ? [] : [actor]}
          max={1}
          onClose={() => setLibrary(false)}
          onConfirm={(picked) => {
            chooseActor(picked[0] ?? null);
            setLibrary(false);
          }}
        />
      ) : null}
    </GlassPanel>
  );
}
